<template>
  <div
    :class="producerClasses"
    :data-cube="cube.cubeName.value"
    :id="`p_${cube.cubeName.value}`"
  >
    <div class="producer__left">
      <router-link :to="mapRoute">
        <img
          class="producer__img"
          :src="cube.iconUrlSvg.value"
        />
        <h4 class="producer__title">{{ cube.nickName }}</h4>
        <span
          v-if="unreadNotifications.length > 0"
          class="producer__has-notification"
          :title="t('notifications.title')"
          @click.prevent.stop="showNotifications = !showNotifications"
        ></span>
      </router-link>
      <span class="producer__has-like">
        <a
          @click="cube.toggleLike()"
          :class="toggleLikeClasses"
          v-if="cube.isLikeable.value || cube.isLikedByMe.value"
          :title="cube.isLikedByMe.value ? t('efriends.unlike') : t('efriends.like')"
        >
          <IconHeartFull v-if="cube.isLikedByMe.value" />
          <IconHeartOutline v-else />
        </a>
        <p>{{ cube.numLikes }}</p>
      </span>
    </div>
    <div class="producer__right">
      <div class="producer__content">
        <div class="producer__content-top">
          <div class="producer__priority">
            <div class="producer__priority-title">{{ t('efriends.priority') }}</div>
            <Choice
              name="priority"
              :default="priority"
              :items="priorities"
              :use-select="true"
              :show-edit-button="!isLocked"
              @selection-changed="onPriorityChanged"
              ref="connection-priority"
            />
            <span v-if="isLocked" class="producer__priority-locked">
              <IconLock />
              <template v-if="!isUserLock">
                {{ lockTitle }}
              </template>
            </span>
          </div>
        </div>
        <a
          :class="toggleActionsClasses"
          @click.stop="toggleActions()"
          :title="t('efriends.actions')"
          v-if="showToggleActions"
        >
          <IconVerticalEllipsis />

          <div :class="actionsClasses" @click.stop="">
            <a
              :class="disconnectLinkClasses"
              @click="disconnectFromProducer()"
              v-if="!isLocked"
            >
              <IconTimes />
              {{ t('efriends.removeConnectionButton') }}
            </a>
            <a
              :class="lockLinkClasses"
              @click="lockConnection()"
              v-if="!isLocked && showUnlockAction"
            >
              <IconLock />
              {{ t('efriends.lock.acquireLock') }}
            </a>
            <a
              :class="lockLinkClasses"
              @click="unlockConnection()"
              v-if="isLocked && isUserLock"
            >
              <IconLockOpen />
              {{ t('efriends.lock.releaseLock') }}
            </a>
          </div>
        </a>
        <div class="producer__content-bottom">
          <div v-if="showGridFeeDiscountIcon && cube.producerPricesLoadedFresh.value" class="producer__grid-fee-discount">
            <img class="producer__grid-fee-discount-icon" :src="assetPath('netzkostenersparnis.svg')" :alt="t('efriends.gridFeeDiscountApplies', {'@price': cube.gridFeeDiscountPriceBrutto.value})" :title="t('efriends.gridFeeDiscountApplies', {'@price': cube.gridFeeDiscountPriceBrutto.value})" />
          </div>
          <output class="producer__current-price">
            <Price
              v-if="cube?.productionType?.value != 'eeg'"
              :label="t('efriends.currentPrice')"
              :value="cube.currentPrice.value"
              :loading="cube.hasProduction.value && !cube.producerPricesLoadedFresh.value"
            />
            <Price
              v-else
              :label="t('efriends.currentPrice')"
              noValueLabel="efriends.priceFromEEG"
            />
          </output>
          <output class="producer__next-price" v-if="cube.producerPricesLoadedFresh.value && cube.hasNextPrice.value">
            <Price :label="cube.nextPriceLabel.value" :value="cube.nextPrice.value" />
          </output>
        </div>

        <div class="producer__fields">
          <div class="producer__capacity">
            <div class="producer__label">{{ t('efriends.capacity') }}</div>
            <div class="producer__value">
              {{ cube.productionCapacity }}
            </div>
          </div>

          <div class="producer__energy-lines">
            <div class="producer__label">{{ t('efriends.used') }}</div>
            <div class="producer__value">
              {{ cube.energyLinesRequired }}
              <span class="producer__value-subtitle">
                {{ t('efriends.energyLine') }}{{ cube.energyLinesRequired.value > 1 ? 's' : '' }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div :class="submitClasses">
        <a class="producer__cancel" @click="cancel()">{{ t('global.cancel') }}</a>

        <LinkButton
          type="small"
          icon-right="Arrow"
          :extra-classes="saveButtonClasses"
          :text="t('global.save')"
          @click.native="saveChanges()"
        />
      </div>
    </div>
    <CubeNotifications
      v-if="showNotifications"
      :notifications="unreadNotifications"
      :cube-name="cube.cubeName.value"
      :extra-classes="['producer__notifications']"
    />
  </div>
</template>

<script lang="ts">
import IconVerticalEllipsis from '@/components/icons/VerticalEllipsis.vue'
import IconTimes from '@/components/icons/Times.vue'
import IconLock from '@/components/icons/Lock.vue'
import IconLockOpen from '@/components/icons/LockOpen.vue'
import Choice from '@/components/Choice.vue'
import Price from '@/components/Price.vue'
import LinkButton from '@/components/LinkButton.vue'
import CubeNotifications from '@/components/efriends/CubeNotifications.vue'
import IconHeartFull from '@/components/icons/HeartFull.vue'
import IconHeartOutline from '@/components/icons/HeartOutline.vue'
import debounce from 'debounce'
import { CONNECTION_PRIORITIES } from '@/constants'
import { useFormsStore } from '@/stores/useFormsStore'
import { useMapStore } from '@/stores/useMapStore'
import { useCubeStore } from '@/stores/useCubeStore'
import { useStrings } from '@/composables/useStrings.ts'
import { useRouter, useRoute } from 'vue-router'
import { useDate } from '@/composables/useDate.ts'
import { useUnits } from '@/composables/useUnits.ts'
import { useSpecificCube } from '@/composables/useSpecificCube.ts'
import { computed, toRef, useTemplateRef } from 'vue'
import { assetPath } from '@/util'
import { useMyCube } from '@/composables/useMyCube.js'
import type { TemplateRef } from 'vue';

const debouncedDisconnect = debounce((cubeStore, producer) => {
  cubeStore.disconnectFromCube(producer)
}, 200)

export default {
  name: 'producer',
  props: {
    producer: {
      type: Object,
      required: true,
    },
  },
  components: {
    IconHeartOutline,
    IconHeartFull,
    IconVerticalEllipsis,
    IconTimes,
    IconLock,
    IconLockOpen,
    Choice,
    Price,
    LinkButton,
    CubeNotifications,
  },
  setup(props) {
    const formsStore = useFormsStore()
    const mapStore = useMapStore()
    const cubeStore = useCubeStore()
    const router = useRouter()
    const currentRoute = useRoute()
    const { t } = useStrings()
    const { formatPriceDate } = useDate()
    const { formatKWP } = useUnits()
    const myCube = useMyCube()

    const connectionPriority: TemplateRef<typeof Choice> = useTemplateRef('connection-priority')

    const mapRoute = {
      name: 'map',
      params: {
        selected: props.producer.cubeName,
      },
    }

    const cubeRef = toRef(props.producer as any)
    const cube = useSpecificCube(router, cubeRef)

    const showGridFeeDiscountIcon = computed(() => {
      return myCube.isGrantedGridFeeDiscount.value && cube.gridFeeDiscountApplies.value
    })

    return {
      cubeStore,
      mapStore,
      formsStore,
      t,
      router,
      currentRoute,
      formatPriceDate,
      formatKWP,
      mapRoute,
      connectionPriority,
      assetPath,
      showGridFeeDiscountIcon,
      cube,
    }
  },
  data() {
    return {
      showActions: false,
      disconnecting: false,
      originalPriority: null,
      priority: null,
      bodyOnClickHandler: null,
      showNotifications: false,
    }
  },
  created() {
    this.initializeValues()
  },
  computed: {
    producerClasses() {
      let result = ['producer']
      const cubeNameRegex = new RegExp(`p_${this.producer.cubeName}`)
      if (
        this.currentRoute.name === 'efriends' &&
        this.currentRoute.hash.match(cubeNameRegex)
      ) {
        result.push('producer--highlighted')
      }
      return result.join(' ')
    },
    priorities: function () {
      if (this.producer.connectionMetadata.lockType === 'EG') {
        return [{ title: 'EG', value: 'EG' }]
      }
      if (this.producer.connectionMetadata.isLocked) {
        return CONNECTION_PRIORITIES.filter(
          (p) => p.value === this.priority
        )
      }
      return CONNECTION_PRIORITIES
    },
    actionsClasses: function () {
      let result = ['producer__actions']
      if (this.showActions) {
        result.push('producer__actions--visible')
      }
      return result.join(' ')
    },
    toggleActionsClasses: function () {
      let result = ['producer__toggle-actions']
      if (this.showActions) {
        result.push('producer__toggle-actions--active')
      }
      return result.join(' ')
    },
    disconnectLinkClasses: function () {
      let classes = ['producer__action-disconnect']
      if (this.disconnecting) {
        classes.push('producer__action-disconnect--disconnecting')
      }
      return classes.join(' ')
    },
    toggleLikeClasses: function() {
      let classes = [];
      if (this.cube.isLikedByMe.value) {
        classes.push('producer__action-like__liked')
      }
      else {
        classes.push('producer__action-like__unlike')
      }
      return classes.join(' ')
    },
    lockLinkClasses: function () {
      let classes = ['producer__action-disconnect']
      return classes.join(' ')
    },
    nextPriceLabel: function () {
      return `Ab ${this.formatPriceDate(this.cube.dateNextPrice.value)}`
    },
    submitClasses: function () {
      let classes = ['producer__submit-buttons']
      if (this.isChanged()) {
        classes.push('producer__submit-buttons--expanded')
      }
      return classes.join(' ')
    },
    saveButtonClasses: function () {
      let classes = ['button--primary producer__save']
      if (
        this.formsStore.isFormSubmitting(
          `producers.${this.producer.cubeName}`
        )
      ) {
        classes.push('producer__actions--submitting')
      }
      return classes
    },
    pricesFromStore: function () {
      return this.mapStore.getProducerPrices(this.producer.cubeName)
    },
    unreadNotifications: function () {
      return this.cubeStore.notifications.filter((notification) => {
        return notification.triggeringcubename === this.producer.cubeName && notification.unread === 1
      })
    },
    isLocked: function () {
      return this.producer.connectionMetadata.isLocked
    },
    isUserLock: function () {
      return this.producer.connectionMetadata.lockType === 'user'
    },
    lockTitle: function () {
      return this.t(
        'efriends.lock.lockTypes.' + this.producer.connectionMetadata.lockType
      )
    },
    //basically show the toggle menu if there is a lock but it's a user lock or show it if there is no lock
    showToggleActions: function () {
      return (
        (this.producer.connectionMetadata.isLocked &&
          this.producer.connectionMetadata.lockType === 'user') ||
        !this.producer.connectionMetadata.isLocked
      )
    },
    showUnlockAction: function () {
      return (
        (this.producer.connectionMetadata.isLocked &&
          this.producer.connectionMetadata.lockType === 'user') ||
        !this.producer.connectionMetadata.isLocked
      )
    },
  },
  methods: {
    initializeValues: function () {
      this.priority =
        this.producer.connectionMetadata.lockType === 'EG'
          ? 'EG'
          : this.cubeStore.connectionPriorityFromCube(this.producer)

      this.originalPriority = this.priority
    },
    toggleActions: function () {
      this.showActions = !this.showActions
      if (this.showActions) {
        this.bodyOnClickHandler = this.hideActions.bind(this)
        document.body.addEventListener('click', this.bodyOnClickHandler)
      } else {
        document.body.removeEventListener('click', this.bodyOnClickHandler)
      }
    },
    hideActions: function () {
      this.showActions = false
      document.body.removeEventListener('click', this.bodyOnClickHandler)
    },
    disconnectFromProducer() {
      this.disconnecting = true
      debouncedDisconnect(this.cubeStore, this.producer)
    },
    lockConnection() {
      this.cubeStore.lockConnection({ producer: this.producer, lock: true })
    },
    unlockConnection() {
      this.cubeStore.lockConnection({ producer: this.producer, lock: false })
    },
    onPriorityChanged: function (newPriority) {
      this.priority = newPriority
    },
    isChanged: function () {
      return this.priority !== this.originalPriority
    },
    cancel: function () {
      this.priority = this.originalPriority
      this.connectionPriority.value = this.originalPriority
      this.connectionPriority.showSelect = false
    },
    saveChanges: async function () {
      this.cubeStore.setConnectionPriority({
        cubeName: this.producer.cubeName,
        priority: this.priority,
        forConsumers: false,
      })
      this.originalPriority = this.priority
      this.connectionPriority.showSelect = false
    },
  },
}
</script>

<style lang="scss" scoped>
$producer-padding-top: $spacer10;

.producer {
  display: flex;
  flex-wrap: wrap;
  padding: $producer-padding-top 0;
  position: relative;

  &--highlighted {
    background: $grey-lightest;
  }

  &__left {
    margin-right: $spacer10;
    text-align: center;
    transition: $transition-base;
    flex-shrink: 0;
    width: $cube-list-img-size-min;
    cursor: pointer;
    position: relative;

    @media (min-width: $bp-xs) {
      width: $cube-list-img-size-xs;
    }

    @media (min-width: $bp-sm) {
      flex-basis: $efriends-page-icon-width-sm;
    }
  }

  &__img {
    padding: 0;
    max-width: 100%;

    @media (min-width: $bp-ssm) {
      padding: 0 $spacer05;
    }

    @media (min-width: $bp-sm) {
      padding: 0 $spacer20;
    }
  }

  &__right {
    flex-grow: 1;
    margin-right: $spacer20;

    @media (min-width: $bp-md) {
      margin-right: $spacer30;
      // Only when setting a width that is smaller than the available space
      // this will fit onto the same line as left.
      width: 30%;
    }
  }

  &__title {
    margin: 0;
    text-align: center;
    font-size: $font-size-sm;
    line-height: $line-height-sm;
    word-break: break-all;
    transition: $transition-base;
    font-weight: normal;

    @media (min-width: $bp-ssm) {
      font-size: $font-size-base;
    }
  }

  &__has-notification {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 0.75em;
    height: 0.75em;
    top: $spacer01 * -1;
    left: $spacer01 * -1;
    position: absolute;
    background: $notification-color;
    padding: 0.25em;
    border-radius: 100%;
    color: $white;

    @media (min-width: $bp-sm) {
      top: $spacer05;
      left: $spacer10;
      width: 1em;
      height: 1em;
    }
  }
  &__has-like {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 0.75em;
    height: 0.75em;
    top: $spacer01 * -1;
    right: $spacer01 * -1;
    position: absolute;

    @media (min-width: $bp-sm) {
      right: $spacer05;
      width: 2em;
      height: 2em;
    }
    .icon {
      width: 1.5em;
    }
  }
  &__fields {
    display: flex;
    flex-wrap: wrap;
    margin-top: $spacer025;

    @media (min-width: $bp-md) {
      justify-content: flex-end;
      flex-basis: 100%;
    }
  }

  &__label {
    font-size: $font-size-sm;
    color: $grey-dark;

    @media (min-width: $bp-xs) {
      font-size: $font-size-base;
    }
  }

  &__value {
    line-height: $line-height-sm;
    font-size: $font-size-base;

    @media (min-width: $bp-sm) {
      font-size: $font-size-lg;
    }

    @media (min-width: $bp-md) {
      font-size: $font-size-xl;
      margin-top: $spacer01;
    }

    &-subtitle {
      @media (max-width: ($bp-xs - 1px)) {
        font-size: 80%;
      }
    }
  }

  &__grid-fee-discount {
    margin-left: auto;
    margin-right: $spacer075;
    display: flex;
    align-items: center;

    &-icon {
      width: 3rem;
      height: auto;
    }
  }

  &__capacity,
  &__energy-lines {
    font-size: $font-size-base;
    line-height: $line-height-sm;
    margin-bottom: $spacer025;
    transition: $transition-base;
    flex-basis: $price-min-width-min;

    @media (min-width: $bp-xs) {
      font-size: $font-size-lg;
      margin-bottom: 0;
      flex-basis: $price-min-width-xs;
    }

    @media (min-width: $bp-ssm) {
      flex-basis: $price-min-width-ssm;
    }

    @media (min-width: $bp-md) {
      font-size: $font-size-xl;
      margin-top: $spacer01;
    }
  }

  &__toggle-actions {
    color: $grey-light;
    cursor: pointer;
    transition: $transition-base;
    width: $spacer20;
    padding: 0 $spacer05;
    align-self: flex-end;
    position: absolute;
    top: $producer-padding-top + 1.15rem;
    right: 0;

    @include hover-focus-active {
      color: $community;
    }

    &--active {
      color: $community;
    }

    .icon {
      width: $spacer05;
    }
  }

  &__actions {
    position: absolute;
    right: 0;
    top: 100%;
    left: auto;
    overflow: hidden;
    transition: opacity 0.3s ease 0s, visibility 0s linear 0.5s;
    padding: $spacer05 $spacer10;
    border: 1px solid $grey-light;
    border-radius: $border-radius;
    opacity: 0;
    width: 200px;
    background: $white;
    visibility: hidden;

    @media (min-width: $bp-sm) {
      right: $spacer05;
    }

    &--visible {
      visibility: visible;
      transition: visibility 0s linear 0s, opacity 0.3s ease 0.1s;
      opacity: 1;
      box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.1);
    }

    &-overlay {
      position: fixed;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
    }
  }

  &__action-disconnect {
    cursor: pointer;
    transition: $transition-base;

    @include hover-focus-active {
      color: $error-color;
    }

    .icon {
      margin-right: $spacer025;
      width: 0.7rem;
      vertical-align: middle;
    }

    &--disconnecting {
      .icon {
        animation: spin 2s linear 0s infinite;
      }
    }
  }
  &__action-like {
    cursor: pointer;
    transition: $transition-base;

    @include hover-focus-active {
      color: $error-color;
    }

    .icon {
      margin-right: $spacer025;
      width: 0.7rem;
      vertical-align: middle;
    }

    &--disconnecting {
      .icon {
        animation: spin 2s linear 0s infinite;
      }
    }

    &__liked {
      color: $error-color;
    }

    &__unliked {
      @include hover-focus-active {
        color: $error-color;
      }
    }
  }
  &__action-unlike {
    cursor: pointer;
    transition: $transition-base;

    @include hover-focus-active {
      color: $error-color;
    }

    .icon {
      margin-right: $spacer025;
      width: 0.7rem;
      vertical-align: middle;
    }

    &--disconnecting {
      .icon {
        animation: spin 2s linear 0s infinite;
      }
    }
  }

  &__priority {
    margin-right: $spacer05;

    &-title {
      font-size: $font-size-sm;
      line-height: $line-height-sm;
      color: $grey-dark;
    }

    &-locked {
      margin-left: $spacer05;
          vertical-align: top;
    }
  }

  &__content {
    flex-grow: 1;

    @media (min-width: $bp-md) {
      display: flex;
      flex-wrap: wrap;
    }

    &-top {
      display: flex;
      flex-wrap: wrap;
    }

    &-bottom {
      margin-top: $spacer10;
      display: flex;
      justify-self: flex-end;
      margin-left: auto;
      flex-grow: 1;
      flex-basis: 40%;

      @media (min-width: $bp-md) {
        margin-top: 0;
        justify-content: flex-end;
      }
    }
  }

  &__submit-buttons {
    max-height: 0;
    overflow: hidden;
    transition: $transition-base;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: $spacer05;
    // The __right element has a margin-right for producers to accomodate for
    // the toggle actions button. We still want the submit buttons to be at
    // the very right though.
    margin-right: $spacer20 * -1;

    @media (min-width: $bp-md) {
      justify-content: flex-end;
      flex-basis: 100%;
    }

    &--expanded {
      max-height: 60px;
    }
  }

  &__cancel {
    cursor: pointer;
    transition: $transition-base;
  }

  &__save {
    align-self: flex-end;
    margin-left: $spacer10;
  }
}

.icon {
  width: 0.8rem;
}
</style>
